import { action, makeObservable, observable, runInAction, when } from 'mobx';
import { socialAuthError, socialAuthSuccess } from '@wix/bi-logger-hls2/v2';
import {
  StateMachineResponse,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { RootStore } from './root';
import { IdpPopupError, openIdpPopup } from '../utils/openIdpPopup';
import {
  consumeIdpRedirectReturn,
  prepareIdpRedirect,
} from '../utils/idpRedirect';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractRedirectUrl,
} from '../utils/errorHandler';
import { handleSsoRedirect } from '../utils/handleSsoRedirect';
import {
  IDP_POPUP_BI_VERSION,
  IDP_REDIRECT_BI_VERSION,
  idpPopupOpenInteraction,
  providerIdFromIdpConnectionId,
  WIX_DEFAULT_ORGANIZATION_ID,
} from '../utils/constants';
import { ROUTES } from '../routes';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import { createBiLogger } from '../services/biLogger';
import {
  IdpOrganizationSelection,
  RequireOrganizationSelectionData,
} from '../types';
import {
  shouldRouteBackToLoginInApp,
  shouldFallbackToIdpRedirect,
} from './constants';

const REQUIRE_FIRST_FACTOR_STATE = 'REQUIRE_FIRST_FACTOR';

function extractEmailFromIdpResponse(
  response: StateMachineResponse,
): string | undefined {
  // IAM v1 standard identity email
  if (response.identity?.email?.address) {
    return response.identity.email.address;
  }
  // Legacy identity profile format (multiple emails array)
  if (response.identity?.identityProfile?.emails?.[0]) {
    return response.identity.identityProfile.emails[0];
  }
  // CommonCustomValue in additionalData (some IDP providers return email here)
  if (response.additionalData?.email?.strValue) {
    return response.additionalData.email.strValue;
  }
  return undefined;
}

const CLIENT_ERROR_NAME = /^[A-Za-z]{1,64}$/;

// Read the name rather than test instanceof Error: a DOMException or an error
// from another realm can fail instanceof. The pattern keeps arbitrary strings
// out of the logs.
function clientErrorName(error: any): string | undefined {
  const errorName = error?.name;
  return typeof errorName === 'string' && CLIENT_ERROR_NAME.test(errorName)
    ? errorName
    : undefined;
}

// extractServerErrorCode falls back to the general code for anything that is
// not a server error, which would hide why the popup itself failed. Client
// errors are reported by name only: their messages can quote the decrypted
// login response (e.g. a JSON.parse SyntaxError), which carries tokens.
function socialAuthErrorReason(error: any, errorCode: string): string {
  if (error instanceof IdpPopupError) {
    return error.reason;
  }
  if (errorCode && errorCode !== ERROR_CODES.GENERAL_ERROR_CODE) {
    return errorCode;
  }
  const errorName = clientErrorName(error);
  if (errorName) {
    return `client_error:${errorName}`;
  }
  return errorCode || 'Unknown error';
}

// The IDP popup may return state as a structured object rather than a plain StateType enum value
interface IamStateObject {
  status?: string;
  stateToken?: string;
}

// Private field, so not in the ambassador types: the organization of the
// account the IdP identity is merged into.
type FirstFactorResponse = StateMachineResponse & {
  requireFirstFactorData?: { organizationId?: string };
};

type OrganizationSelectionResponse = StateMachineResponse & {
  requireOrganizationSelectionData?: RequireOrganizationSelectionData;
};

/**
 * Store for handling IDP (Identity Provider) connection login flow.
 * Similar to social login but uses the IDP popup flow for authentication.
 */
export class IdpConnectionLoginStore {
  protected readonly rootStore: RootStore;
  isLoading: boolean = false;
  hasError: boolean = false;
  errorCode: string = '';
  isIdpFirstFactorFlow: boolean = false;
  // Covers only click → popup open (~200ms): a guard held until login ends
  // would lock the button if the user closes the popup themselves.
  private isOpeningPopup = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      isLoading: observable,
      hasError: observable,
      errorCode: observable,
      isIdpFirstFactorFlow: observable,
      loginWithIdpConnection: action.bound,
      handleError: action.bound,
      clearErrors: action.bound,
    });
  }

  /**
   * Initiates login via an Identity Provider connection.
   *
   * @param idpConnectionId - The unique identifier of the IDP connection
   * @param ldSessionId - The login dialog session ID for tracking
   * @returns Promise resolving to login result with success status
   */
  async loginWithIdpConnection(
    idpConnectionId: string,
    ldSessionId: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    // A second click while the first is still opening would start a second
    // same-named transaction that replaces the first, so one of the two never
    // finishes and reads as a failed popup.
    if (this.isOpeningPopup) {
      return { success: false };
    }
    this.isOpeningPopup = true;
    let popupOpened = false;

    this.isLoading = true;
    this.hasError = false;
    this.errorCode = '';

    const popupOpenInteraction = idpPopupOpenInteraction(
      providerIdFromIdpConnectionId(idpConnectionId),
    );

    try {
      this.rootStore.panorama.startTransaction(popupOpenInteraction);
      const loginResponse = await openIdpPopup({
        idpConnectionId,
        ldSessionId,
        extraCustomPayload:
          this.rootStore.apiStore.buildIdpCustomPayloadExtras(),
        onPopupOpened: () => {
          popupOpened = true;
          this.isOpeningPopup = false;
          this.rootStore.panorama.finishTransaction(popupOpenInteraction);
        },
      });

      return await this.handleLoginSuccess(loginResponse, idpConnectionId);
    } catch (error: any) {
      // Failures the popup flow does not anticipate would otherwise leave the
      // popup-open transaction unfinished with no reason logged.
      if (!popupOpened && !(error instanceof IdpPopupError)) {
        this.rootStore.panorama.warn('idp-popup-failed:unexpected', {
          provider: providerIdFromIdpConnectionId(idpConnectionId),
          reason: 'unexpected',
          errorName: clientErrorName(error),
        });
      }
      return await this.handleLoginError(error, idpConnectionId, {
        ldSessionId,
      });
    } finally {
      this.isLoading = false;
      if (!popupOpened) {
        this.isOpeningPopup = false;
      }
    }
  }

  /**
   * @param idpEmail - The IdP's email when the response may not carry it: a
   * SelectOrganization response has no identity.
   */
  private async handleLoginSuccess(
    loginResponse: StateMachineResponse,
    idpConnectionId: string,
    biVersion: string = IDP_POPUP_BI_VERSION,
    idpEmail?: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    const stateObj = loginResponse.state as unknown as IamStateObject;
    const stateValue =
      stateObj?.status ?? (loginResponse.state as unknown as string);

    if (stateValue === REQUIRE_FIRST_FACTOR_STATE) {
      return this.handleFirstFactorRequired(
        loginResponse,
        idpConnectionId,
        idpEmail,
      );
    }

    if (stateValue === StateType.REQUIRE_ORGANIZATION_SELECTION) {
      const selection = this.organizationSelectionOf(
        loginResponse,
        idpConnectionId,
      );
      if (selection) {
        return this.handleOrganizationSelectionRequired(selection);
      }
    }

    // Check for MFA challenge
    if (stateValue === StateType.MFA_CHALLENGE && loginResponse.stateToken) {
      this.rootStore.twoFactorAuthStore.startChallenge(loginResponse, true);
      return { success: false };
    }

    // Only proceed to success if state is SUCCESS or DONE
    const isSuccessState =
      stateValue === StateType.SUCCESS ||
      stateValue === 'SUCCESS' ||
      stateValue === 'DONE';
    if (!isSuccessState) {
      const reason = `Unexpected state: ${stateValue}`;
      this.rootStore.biLogger.report(
        socialAuthError({
          socialNetwork: providerIdFromIdpConnectionId(idpConnectionId),
          reason,
          version: biVersion,
        }),
      );
      this.handleError(reason);
      return { success: false };
    }

    this.reportLoginSuccess(idpConnectionId, biVersion);

    const additionalData = loginResponse.additionalData ?? {};
    const postAuthParams = {
      uaToken: additionalData.uaToken?.strValue,
      authMethod: additionalData.authMethod?.strValue,
    };
    if (additionalData.isNewIdentity?.numValue === 1) {
      this.rootStore.navigationStore.postSignup(postAuthParams);
    } else {
      this.rootStore.navigationStore.postLogin(postAuthParams);
    }

    return { success: true };
  }

  private reportLoginSuccess(idpConnectionId: string, version: string) {
    this.rootStore.biLogger = createBiLogger();
    this.rootStore.postLoginStore.biLogger = createBiLogger();

    this.rootStore.biLogger.report(
      socialAuthSuccess({
        socialNetwork: providerIdFromIdpConnectionId(idpConnectionId),
        context: this.rootStore.emailStepStore.getContext(),
        version,
      }),
    );
  }

  /**
   * Sends the user through the IdP in this window instead of a popup. Returns
   * false when the fallback is off or cannot run, leaving the error to show.
   */
  private startIdpRedirect(idpConnectionId: string, ldSessionId: string) {
    const { env } = this.rootStore;
    // Inside an iframe the navigation would only move the frame, and IdPs
    // refuse to be framed.
    if (
      !shouldFallbackToIdpRedirect(this.rootStore.experiments) ||
      env.isIframe
    ) {
      return false;
    }
    const url = prepareIdpRedirect(env, {
      idpConnectionId,
      ldSessionId,
      extraCustomPayload: this.rootStore.apiStore.buildIdpCustomPayloadExtras(),
    });
    this.rootStore.panorama.info('idp-redirect-started', {
      provider: providerIdFromIdpConnectionId(idpConnectionId),
      started: Boolean(url),
    });
    if (!url) {
      return false;
    }
    env.navigateExternal(url);
    return true;
  }

  /**
   * Completes a same-tab IdP redirect this tab started, when the page was
   * loaded as its return: the IAM callback left the result the popup would
   * have received, and it is handled exactly as the popup's.
   */
  async resumeIdpRedirect(): Promise<void> {
    const result = consumeIdpRedirectReturn(this.rootStore.env);
    if (!result) {
      return;
    }
    const { idpConnectionId } = result;
    const provider = providerIdFromIdpConnectionId(idpConnectionId);
    this.rootStore.panorama.info('idp-redirect-returned', {
      provider,
      outcome: result.status,
    });
    if (result.status === 'abandoned') {
      return;
    }

    const { success, errorCode } =
      result.status === 'completed'
        ? await this.handleLoginSuccess(
            result.loginResponse,
            idpConnectionId,
            IDP_REDIRECT_BI_VERSION,
          )
        : await this.handleLoginError(result.error, idpConnectionId, {
            biVersion: IDP_REDIRECT_BI_VERSION,
          });
    // The popup flow mirrors the error in handleIdpPopupClick.
    if (!success && this.hasError) {
      this.showErrorUnderProvider(provider, errorCode ?? this.errorCode);
    }
  }

  /** The inline error under the IdP's button is rendered from the provider's own store. */
  private showErrorUnderProvider(provider: string, errorCode: string) {
    const providerStore = this.rootStore.getSocialProviderStore(provider);
    if (providerStore) {
      runInAction(() => {
        providerStore.hasError = true;
        providerStore.errorCode = errorCode;
      });
    }
  }

  private organizationSelectionOf(
    response: StateMachineResponse,
    idpConnectionId: string,
  ): IdpOrganizationSelection | undefined {
    const stateObj = response.state as unknown as IamStateObject;
    const stateToken = stateObj?.stateToken ?? response.stateToken;
    const { email, organizations } =
      (response as OrganizationSelectionResponse)
        .requireOrganizationSelectionData ?? {};
    return stateToken && email && organizations?.length
      ? { idpConnectionId, stateToken, email, organizations }
      : undefined;
  }

  /**
   * The IdP's email has users in several organizations this login can
   * continue in: the account selector offers them, and the pick continues the login
   * (continueWithOrganization) without asking the IdP again.
   */
  private async handleOrganizationSelectionRequired(
    selection: IdpOrganizationSelection,
  ): Promise<{ success: boolean; errorCode?: string }> {
    // Without the in-app path the picked organization can't reach the first
    // factor, so the personal account continues, as before there was a pick.
    if (!shouldRouteBackToLoginInApp(this.rootStore.experiments)) {
      return this.selectOrganization(selection, WIX_DEFAULT_ORGANIZATION_ID);
    }
    if (selection.organizations.length === 1) {
      return this.selectOrganization(
        selection,
        selection.organizations[0].organizationId,
      );
    }
    const { userDataStore, selectAccountStore, navigationStore } =
      this.rootStore;
    userDataStore.setEmail(selection.email);
    this.rootStore.setActiveSocialProvider(
      providerIdFromIdpConnectionId(selection.idpConnectionId),
    );
    selectAccountStore.offerIdpOrganizations(selection);
    navigationStore.navigate(ROUTES.SELECT_ACCOUNT);
    return { success: false };
  }

  /**
   * Continues the IdP login waiting for a pick in the organization picked in
   * the account selector. A login that can't continue (its token used or
   * expired, the organization no longer offered) starts over: back to the screen
   * with the IdP's button, the error under it.
   */
  async continueWithOrganization(
    selection: IdpOrganizationSelection,
    organizationId: string,
  ): Promise<void> {
    this.clearErrors();
    const { success, errorCode } = await this.selectOrganization(
      selection,
      organizationId,
    );
    if (!success && this.hasError) {
      this.showErrorUnderProvider(
        providerIdFromIdpConnectionId(selection.idpConnectionId),
        errorCode ?? this.errorCode,
      );
      this.rootStore.navigationStore.goBack();
    }
  }

  private async selectOrganization(
    { idpConnectionId, stateToken, email }: IdpOrganizationSelection,
    organizationId: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    let response: StateMachineResponse;
    try {
      response = await this.rootStore.apiStore.selectOrganization({
        stateToken,
        organizationId,
      });
    } catch (error) {
      return this.handleLoginError(error, idpConnectionId);
    }
    return this.handleLoginSuccess(
      response,
      idpConnectionId,
      IDP_POPUP_BI_VERSION,
      email,
    );
  }

  private handleFirstFactorRequired(
    response: StateMachineResponse,
    idpConnectionId: string,
    idpEmail?: string,
  ): { success: boolean } {
    const email = extractEmailFromIdpResponse(response) ?? idpEmail;

    if (email) {
      this.rootStore.userDataStore.setEmail(email);
    }

    const stateObj = response.state as unknown as IamStateObject;
    const stateToken = stateObj?.stateToken ?? response.stateToken ?? undefined;
    this.rootStore.loginStore.loginFlow.record(
      this.rootStore.userDataStore.email,
      stateToken,
      StateType.REQUIRE_FIRST_FACTOR,
    );

    this.isIdpFirstFactorFlow = true;
    this.rootStore.setActiveSocialProvider(
      providerIdFromIdpConnectionId(idpConnectionId),
    );

    this.rootStore.notificationStore.openWarningToast(
      this.rootStore.i18n.t('account_verification.social_connect_error', {
        email: this.rootStore.userDataStore.email,
      }),
    );
    // The email step's lookup then picks that organization's account instead
    // of opening the account selector. Without a state token there is no
    // merge to continue in it, and without the IdP's email the lookup is of
    // an earlier typed one, not that account's.
    const { emailStepStore, userDataStore } = this.rootStore;
    const organizationId =
      (email &&
        stateToken &&
        shouldRouteBackToLoginInApp(this.rootStore.experiments) &&
        (response as FirstFactorResponse).requireFirstFactorData
          ?.organizationId) ||
      undefined;
    emailStepStore.emailField.setValue(userDataStore.email);
    emailStepStore.submit(true, { organizationId });
    if (
      emailStepStore.isLoading &&
      shouldRouteBackToLoginInApp(this.rootStore.experiments)
    ) {
      // The screen the IdP button was on (e.g. signup) stays up until the
      // lookup routes away.
      this.rootStore.setIsLoading(true);
      when(
        () => !emailStepStore.isLoading,
        () => this.rootStore.setIsLoading(false),
      );
    }
    return { success: false };
  }

  private async handleLoginError(
    error: any,
    idpConnectionId: string,
    {
      ldSessionId,
      biVersion = IDP_POPUP_BI_VERSION,
    }: { ldSessionId?: string; biVersion?: string } = {},
  ): Promise<{ success: boolean; errorCode?: string }> {
    const errorCode = extractServerErrorCode(error);
    const socialNetwork = providerIdFromIdpConnectionId(idpConnectionId);

    if (error instanceof IdpPopupError) {
      this.rootStore.panorama.warn(`idp-popup-failed:${error.reason}`, {
        ...error.details,
        provider: socialNetwork,
        reason: error.reason,
      });
    }

    this.rootStore.biLogger.report(
      socialAuthError({
        socialNetwork,
        reason: socialAuthErrorReason(error, errorCode),
        version: biVersion,
      }),
    );

    if (
      error instanceof IdpPopupError &&
      error.reason === 'popup_blocked' &&
      ldSessionId !== undefined &&
      this.startIdpRedirect(idpConnectionId, ldSessionId)
    ) {
      return { success: false };
    }

    // Handle specific error cases
    if (
      errorCode === ERROR_CODES.USER_IS_BLOCKED ||
      errorCode === ERROR_CODES.ACCOUNT_NOT_FOUND
    ) {
      this.rootStore.navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.SOCIAL,
      });
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR) {
      // User has SSO connection - redirect to SSO login
      const redirectUrl = extractRedirectUrl(error);
      if (redirectUrl) {
        this.rootStore.navigationStore.redirect(redirectUrl);
        return { success: false, errorCode };
      }
    }

    if (errorCode === ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL) {
      // User needs to provide email - navigate to social enter email page
      this.rootStore.setActiveSocialProvider(
        providerIdFromIdpConnectionId(idpConnectionId),
      );
      this.rootStore.navigationStore.navigate(ROUTES.SOCIAL_ENTER_EMAIL);
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.NO_PERSONAL_WIX_ACCOUNT) {
      // The new IAM system returns "NO_PERSONAL_WIX_ACCOUNT" when the social
      // identity authenticated but there is no matching personal Wix account.
      // The popup forwards the account email in errorData.
      const email =
        error.errorData?.email || this.rootStore.userDataStore.email;
      // Without an email we cannot run the SSO check (userAccountsByEmail), so
      // fall through to the generic error below.
      if (email) {
        this.rootStore.userDataStore.setEmail(email);
        this.rootStore.setActiveSocialProvider(
          providerIdFromIdpConnectionId(idpConnectionId),
        );
        // Redirect to SSO if the account mandates it; otherwise there is no
        // account to verify, so fall through to the generic error below.
        const ssoRedirected = await handleSsoRedirect(this.rootStore, email);
        if (ssoRedirected) {
          return { success: false, errorCode };
        }
      }
    }

    // Generic error handling
    this.hasError = true;
    this.errorCode = errorCode || 'UNKNOWN_ERROR';

    return { success: false, errorCode: this.errorCode };
  }

  handleError(error?: string) {
    this.hasError = true;
    this.errorCode = error || 'UNKNOWN_ERROR';
  }

  clearErrors() {
    this.hasError = false;
    this.errorCode = '';
  }
}
